import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { localizeDate } from '@/utils/date';
import get from 'lodash/get';
import { getFileURL } from '../../../utils/file-uploads';
import isObject from 'lodash/isObject';
import VDeviceMixin from '../../../components/core/dtwin/state/virtualDevice/aggregate/mixin.js';
import cloneDeep from 'lodash/cloneDeep';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import codemirror from '@/mixins/codemirror';
import merge from 'lodash/merge';
import set from 'lodash/set';
export default {
  name: 'VirtualDeviceDriverDetails',
  mixins: [VDeviceMixin, multiSearchMixin, codemirror],
  data() {
    return {
      accounts: [],
      linkedDevicesTotalCount: 0,
      showCopyPrompt: false,
      useExpertMode: false,
      activeVersion: 'default',
      showHealthSyncPrompt: false,
      driverIsChanged: false,
      healthIsSyncing: false,
      accountId: null,
      hasPermission: null,
      EDIT_MODE: {
        CREATE: 'CREATE',
        EDIT: 'EDIT',
        READ_ONLY: 'READ_ONLY'
      }
    };
  },
  computed: {
    ...mapState('app', ['permissions']),
    isCopyPasteAction() {
      return this.$route.query.source === 'clipboard';
    },
    driverId() {
      return this.$route.params.id;
    },
    isVirtualDeviceDriverManager() {
      return this.permissions['DeviceDriver.manage'];
    },
    editMode() {
      if (this.driverId === 'new' || this.isCopyPasteAction) {
        return this.EDIT_MODE.CREATE;
      }
      if (this.isVirtualDeviceDriverManager) {
        return this.EDIT_MODE.EDIT;
      }
      return this.EDIT_MODE.READ_ONLY;
    },
    pageIsReadOnly() {
      return this.editMode === this.EDIT_MODE.READ_ONLY || this.hasPermission === false;
    },
    metaInformationTab() {
      const technicalSpecFields = ['manufacturerDeviceName', 'model', 'configuration', 'connectivity', 'frequency', 'txPower', 'txDistance', 'powerSupply', 'operatingTemperature', 'security', 'mounting', 'dimensions', 'weight', 'standard', 'battery', 'batteryLife', 'firmware', 'precision', 'installationHeight', 'usage', 'other'];
      const technicalSpecFieldsGroupedInRowsOfTwo = technicalSpecFields.reduce((acc, field, index) => {
        if (index % 2 === 0) {
          acc.push({
            columns: []
          });
        }
        acc[acc.length - 1].columns.push({
          cols: 6,
          sm: 12,
          md: 6,
          lg: 6,
          field: {
            label: this.$i18n_t(`DeviceDriver.details.tabs.metaInformation.sections.technicalSpecifications.fields.${field}`),
            key: `properties.technical_specs.${field}`
          }
        });
        return acc;
      }, []);
      return {
        label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.title'),
        urlHashbang: 'meta-information',
        rows: [
        // section: title, account, visibility, description
        {
          columns: [{
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            rows: [{
              columns: [{
                cols: 12,
                field: {
                  label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.main.fields.title'),
                  key: 'title',
                  required: true
                }
              }]
            }, {
              columns: [{
                cols: 12,
                slotName: 'accountSelector'
              }]
            }, {
              columns: [{
                cols: 12,
                field: {
                  label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.main.fields.visibility.label'),
                  key: 'visibility',
                  type: 'select',
                  options: [{
                    // account
                    label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.main.fields.visibility.options.account'),
                    value: 'account'
                  }, {
                    // everyone
                    label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.main.fields.visibility.options.everyone'),
                    value: 'everyone'
                  }]
                }
              }]
            }]
          }, {
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.main.fields.description'),
              key: 'description',
              type: 'textarea'
            }
          }]
        },
        // section: "Device Image & Attachments"
        {
          headline: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.deviceImage.title'),
          subtitle: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.deviceImage.subtitle'),
          columns: [{
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.deviceImage.fields.image'),
              key: 'image'
            },
            slotName: 'image'
          }, {
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.deviceImage.fields.attachments'),
              key: 'properties.attachments'
            },
            slotName: 'attachments'
          }]
        },
        // section: "Installation notes & highlights"
        {
          headline: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.installationNotes.title'),
          subtitle: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.installationNotes.subtitle'),
          columns: [{
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.installationNotes.fields.installationNotes'),
              key: 'properties.notes',
              type: 'textarea'
            }
          }, {
            cols: 6,
            sm: 12,
            md: 6,
            lg: 6,
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.installationNotes.fields.highlights'),
              key: 'properties.highlights',
              type: 'textarea'
            }
          }]
        },
        // section: "Technical specifications"
        {
          headline: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.technicalSpecifications.title'),
          subtitle: this.$i18n_t('DeviceDriver.details.tabs.metaInformation.sections.technicalSpecifications.subtitle'),
          columns: [{
            cols: 12,
            rows: technicalSpecFieldsGroupedInRowsOfTwo
          }]
        }]
      };
    },
    packetParserTab() {
      return {
        label: this.$i18n_t('DeviceDriver.details.tabs.packetParser.title'),
        urlHashbang: 'packet-parser',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'packetParserConfigurator'
          }]
        }]
      };
    },
    predefinedDownlinksTab() {
      const makeEmptyConfigItem = () => ({
        name: '',
        port: 0,
        payload: '',
        encoding: 'base16',
        gatewayId: ''
      });
      const makeDownlinkItemRow = (activeVersionIndex, downlinkItemIndex, currentDownlinkItems) => {
        const fieldDefinitions = [{
          cols: 5,
          sm: 12,
          md: 5,
          lg: 5,
          field: {
            key: 'name',
            type: 'string',
            required: true
          }
        }, {
          cols: 2,
          sm: 6,
          md: 2,
          lg: 2,
          field: {
            key: 'port',
            type: 'number',
            required: true
          }
        }, {
          cols: 4,
          sm: 6,
          md: 4,
          lg: 4,
          field: {
            key: 'gatewayId'
          }
        }, {
          cols: 7,
          field: {
            key: 'payload',
            type: 'string',
            required: true
          }
        }, {
          cols: 4,
          field: {
            key: 'encoding',
            type: 'select',
            options: ['base16', 'base64', 'utf8'],
            required: true
          }
        }];
        return {
          columns: [{
            cols: 11,
            rows: [{
              columns: fieldDefinitions.map(fieldDefinition => {
                return {
                  ...fieldDefinition,
                  field: {
                    ...fieldDefinition.field,
                    label: fieldDefinition.field.label || fieldDefinition.field.key && this.$i18n_t(`DeviceDriver.details.tabs.predefinedDownlinks.fields.${fieldDefinition.field.key}`),
                    key: `versions[${activeVersionIndex}].downlinks[${downlinkItemIndex}]['${fieldDefinition.field.key}']`,
                    options: (fieldDefinition.field.options || []).map(option => ({
                      label: this.$i18n_t(`DeviceDriver.details.tabs.predefinedDownlinks.optionValues.${fieldDefinition.field.key}.${option}`),
                      value: option
                    }))
                  }
                };
              })
            }]
          }, {
            cols: 1,
            rows: [{
              columns: [{
                cols: 12,
                field: {
                  type: 'button',
                  icon: 'fal fa-plus',
                  onClick: function (updateField) {
                    const newConfigItems = [...currentDownlinkItems];
                    newConfigItems.splice(downlinkItemIndex + 1, 0, makeEmptyConfigItem());
                    updateField(`versions[${activeVersionIndex}].downlinks`, newConfigItems);
                  }
                }
              }]
            }, {
              columns: [{
                cols: 12,
                field: {
                  type: 'button',
                  icon: 'fal fa-trash',
                  onClick: function (updateField) {
                    const newConfigItems = currentDownlinkItems.filter((_, index) => index !== downlinkItemIndex);
                    updateField(`versions[${activeVersionIndex}].downlinks`, newConfigItems);
                  }
                }
              }]
            }]
          }]
        };
      };
      return currentValue => {
        const activeVersionIndex = this.getActiveVersionIndex(currentValue);
        const downlinks = cloneDeep(get(currentValue, `versions[${activeVersionIndex}].downlinks`, []));
        return {
          label: this.$i18n_t('DeviceDriver.details.tabs.predefinedDownlinks.title'),
          urlHashbang: 'predefined-downlinks',
          rows: [{
            subtitle: this.$i18n_t('DeviceDriver.details.tabs.predefinedDownlinks.description')
          }, ...downlinks.map((item, downlinkIndex) => makeDownlinkItemRow(activeVersionIndex, downlinkIndex, downlinks)).map((row, rowIndex) => {
            if (rowIndex >= downlinks.length - 1) {
              return [row];
            }
            return [row, {
              isDivider: true
            }];
          }).flat(), downlinks.length === 0 ? {
            columns: [{
              cols: 12,
              field: {
                type: 'button',
                icon: 'fal fa-plus',
                onClick: function (updateField) {
                  updateField(`versions[${activeVersionIndex}].downlinks`, [...downlinks, makeEmptyConfigItem()]);
                }
              }
            }]
          } : {}]
        };
      };
    },
    linkedDevicesTab() {
      return {
        label: this.$i18n_t('DeviceDriver.details.tabs.linkedDevices.title', {
          count: this.linkedDevicesTotalCount
        }),
        urlHashbang: 'linked-devices',
        eager: true,
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'linkedDevices'
          }]
        }]
      };
    },
    tabs() {
      return [this.metaInformationTab, this.packetParserTab, this.healthStateTab, this.predefinedDownlinksTab, this.linkedDevicesTab];
    },
    availableDurations() {
      const baseTranslation = 'DigitalTwin.DigitalTwin.visualization.timeRanges.count';
      return [15, 30].map(min => {
        return {
          value: min * 60,
          label: this.$i18n_tc(`${baseTranslation}Min`, min, {
            n: min
          })
        };
      }).concat([1, 3, 6, 12, 24].map(hour => {
        return {
          value: hour * 3600,
          label: this.$i18n_tc(`${baseTranslation}Hour`, hour, {
            n: hour
          })
        };
      })).concat([2, 7, 14].map(day => {
        return {
          value: day * 86400,
          label: this.$i18n_tc(`${baseTranslation}Day`, day, {
            n: day
          })
        };
      })).concat([1, 3, 6].map(mon => {
        return {
          value: mon * 30 * 86400,
          label: this.$i18n_tc(`${baseTranslation}Month`, mon, {
            n: mon
          })
        };
      })).concat([{
        value: 365 * 86400,
        label: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1Y')
      }]);
    },
    linkedDevicesPredefinedSearch() {
      return [{
        param: 'deviceDriver',
        search: {
          value: this.driverId
        }
      }];
    },
    syncHealthToVDPromptButtons() {
      const buttons = [{
        text: this.$i18n_t('DeviceDriver.details.prompts.syncHealthToVD.buttons.cancel'),
        emit: 'cancel'
      }];
      if (!this.driverIsChanged) {
        buttons.unshift({
          text: this.$i18n_t('DeviceDriver.details.prompts.syncHealthToVD.buttons.sync'),
          emit: 'sync',
          isLoading: this.healthIsSyncing
        });
      }
      return buttons;
    }
  },
  methods: {
    get,
    localizeDate,
    getActiveVersionIndex(driver) {
      const versions = get(driver, 'versions', []);
      return versions.findIndex(v => v.version === 'default');
    },
    fixMissingAttachments(item) {
      const newItem = cloneDeep(item);
      const image = get(newItem, 'image', null);
      const attachments = get(newItem, 'properties.attachments', []);

      // if image exists and isn't already in attachments, add it
      // this maintains consistency between image references and attachments list
      if (!attachments.some(attachment => attachment.id === image)) {
        attachments.push({
          id: image,
          name: 'Device Image',
          size: null
        });
        set(newItem, 'properties.attachments', attachments);
      }
      return newItem;
    },
    async fetchDriver() {
      let item = {
        title: '',
        description: '',
        accountId: null,
        visibility: 'account',
        properties: {},
        versions: []
      };
      if (this.isCopyPasteAction) {
        try {
          const clipboardContent = await navigator.clipboard.readText();
          item = JSON.parse(clipboardContent);
          delete item.createdAt;
          delete item.updatedAt;
          delete item.id;
          delete item.Tenant;
        } catch (e) {
          console.error(e);
          this.$snack(this.$i18n_t('DeviceDriver.details.copyPasteAction.error.snack.text'), 'error');
          this.$router.push('/virtual-device-drivers');
        }
      } else if (this.editMode !== this.EDIT_MODE.CREATE) {
        const fetchedItem = await ApiService.virtualDeviceDrivers.fetchOneById(this.driverId);
        item = merge(item, fetchedItem);
      }
      if (!item.versions) {
        item.versions = [];
      }
      if (!item.versions.some(v => v.version === 'default')) {
        item.versions.push({
          version: 'default'
        });
      }
      item.description = item.description || '';
      const versionIndex = this.getActiveVersionIndex(item);
      const healthBaseKey = `versions[${versionIndex}].healthStateConfig`;
      const metrics = get(item, `${healthBaseKey}.health`, []);
      if (metrics.length > 0) {
        this.useExpertMode = !metrics.some(m => m.wizard);
      }
      this.accountId = item.accountId;
      this.hasPermission = item.hasPermission;
      return this.fixMissingAttachments(item);
    },
    async saveItem(item) {
      // update wizard property on all metrics, just to make sure...
      const versionIndex = this.getActiveVersionIndex(item);
      const healthBaseKey = `versions[${versionIndex}].healthStateConfig.health`;
      const metrics = get(item, `${healthBaseKey}`, []);
      set(item, healthBaseKey, metrics.map(m => ({
        ...m,
        wizard: !this.useExpertMode
      })));
      if (item.id && !this.isCopyPasteAction) {
        return await ApiService.virtualDeviceDrivers.updateOne(item.id, item);
      }
      return await ApiService.virtualDeviceDrivers.createOne({
        ...item,
        accountId: this.accountId
      });
    },
    headline(item) {
      if (this.editMode === this.EDIT_MODE.CREATE) {
        return this.$i18n_t('DeviceDriver.details.header.title.create');
      }
      return item.title || this.$i18n_t('DeviceDriver.details.header.title.fallback');
    },
    subtitle(item) {
      if (this.editMode === this.EDIT_MODE.CREATE) {
        return this.$i18n_t('DeviceDriver.details.header.subtitle.create');
      }
      return item.account || this.$i18n_t('DeviceDriver.details.header.subtitle.fallback');
    },
    getImageUrl(fileId) {
      return getFileURL(fileId, 2);
    },
    getActiveVersion(item) {
      const activeVersionIndex = this.getActiveVersionIndex(item);
      const activeVersion = get(item, 'versions', [])[activeVersionIndex];
      return activeVersion ? activeVersion : {};
    },
    getConfigForParserWrapper(parser) {
      return parser;
    },
    onSaveParser(newParserConfig, slotProps) {
      const updatedVersion = {
        ...newParserConfig,
        version: this.activeVersion,
        account: undefined
      };
      slotProps.updateFieldByKey((updateByKey, originalItem) => {
        const versions = get(originalItem, 'versions', []);
        const unchangedVersions = versions.filter(v => v.version !== this.activeVersion);
        const newVersions = [...unchangedVersions, updatedVersion];
        updateByKey('versions', newVersions);
      });
    },
    deviceDriverParserTester(slotProps) {
      return async (payload, meta) => {
        const activeVersion = this.getActiveVersion(slotProps.item);
        if (!activeVersion) {
          throw new Error('No default version found');
        }
        return await ApiService.deviceDriverParsePayload(activeVersion.parser, {
          payload,
          meta
        });
      };
    },
    healthStateTab(item, originalItem) {
      const versionIndex = this.getActiveVersionIndex(item);
      const healthBaseKey = `versions[${versionIndex}].healthStateConfig`;
      const metrics = get(item, `${healthBaseKey}.health`, []);
      const healthStateNormalTabRows = this.healthStateNormalTabRows(item, originalItem);
      const healthStateExpertTabRows = this.healthStateExpertTabRows(item, originalItem);
      return {
        label: this.$i18n_t('DeviceDriver.details.tabs.healthState.title'),
        urlHashbang: 'health',
        rows: [{
          columns: [{
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.healthState.fields.expertMode'),
              type: 'switch',
              value: this.useExpertMode,
              onInput: async ({
                updateItemByFieldKey,
                waitForUserConsent,
                rerenderInput
              }) => {
                const hasConsent = await waitForUserConsent(this.$i18n_t('DeviceDriver.details.tabs.healthState.swapExpertModeDialog.title'), this.$i18n_t('DeviceDriver.details.tabs.healthState.swapExpertModeDialog.message'));
                if (!hasConsent) {
                  return;
                }
                updateItemByFieldKey(`${healthBaseKey}.health`, metrics.map(m => ({
                  ...m,
                  wizard: this.useExpertMode
                })));
                this.useExpertMode = !this.useExpertMode;
                rerenderInput();
              }
            }
          }, {
            class: 'flex-grow-0',
            field: {
              label: this.$i18n_t('DeviceDriver.details.tabs.healthState.fields.applyToAllVD'),
              type: 'button',
              readonly: this.linkedDevicesTotalCount === 0 ? true : false,
              onClick: () => {
                this.showHealthSyncPrompt = true;
              }
            }
          }]
        }, ...(this.useExpertMode ? healthStateExpertTabRows : healthStateNormalTabRows)]
      };
    },
    async syncHealthStates() {
      try {
        this.healthIsSyncing = true;
        await ApiService.createBackgroundRunnerBulkEdit({
          entities: [],
          tenantId: this.accountId,
          title: this.jobTitle,
          executions: {
            syncHealthConfigurationFromDeviceDriver: {
              deviceDriverId: parseInt(this.driverId)
            }
          }
        });
        this.$snack(this.$i18n_t('DeviceDriver.details.prompts.syncHealthToVD.snack.success', {
          count: this.linkedDevicesTotalCount
        }), 'success');
      } catch (e) {
        console.error(e);
        this.$snack(this.$i18n_t('DeviceDriver.details.prompts.syncHealthToVD.snack.error'), 'error');
      } finally {
        this.healthIsSyncing = false;
      }
    },
    getParserVariables(driverVersion) {
      let parserVariables = get(driverVersion, 'parser.config.variables', {});
      const isMultiParser = get(driverVersion, 'parser.type') === 'multi';
      if (isMultiParser) {
        parserVariables = {};
        const parserKeys = Object.keys(get(driverVersion, 'parser.config') || {}).filter(k => k !== '__switch');
        parserKeys.forEach(pKey => {
          const parserVariablesOfKey = get(driverVersion, `parser.config.${pKey}.variables`);
          if (isObject(parserVariablesOfKey)) {
            Object.assign(parserVariables, parserVariablesOfKey);
          }
        });
      }

      // Always include _raw as it's fundamental for health checks, especially VALUE_OLDER_THAN
      parserVariables['_raw'] = {
        value_type: {
          name: 'json'
        },
        key: '_raw'
      };
      const mappedParserVariables = Object.keys(parserVariables).map(key => {
        const mapped = this.mapStateToUi(parserVariables[key]);
        return {
          label: mapped.key || key,
          value: mapped.id
        };
      });
      return mappedParserVariables;
    },
    healthStateNormalTabRows(item) {
      const versionIndex = this.getActiveVersionIndex(item);
      const healthBaseKey = `versions[${versionIndex}].healthStateConfig.health`;
      const currentHealthStates = get(item, healthBaseKey, []);
      if (currentHealthStates.length === 0) {
        const emptyState = {
          columns: [{
            class: 'text-center',
            field: {
              type: 'emptystate',
              icon: 'fal fa-sensor-smoke',
              title: this.$i18n_t('DeviceDriver.details.tabs.healthState.emptyState.title'),
              description: this.$i18n_t('DeviceDriver.details.tabs.healthState.emptyState.description'),
              button: this.$i18n_t('DeviceDriver.details.tabs.healthState.emptyState.buttonCreate'),
              onClick: function (updateField) {
                let nextUnusedMetricID = 0;
                while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                  nextUnusedMetricID++;
                }
                const emptyState = {
                  type: 'warning',
                  // TODO: how to handle this?
                  jsTransformer: '',
                  wizard: true,
                  operator: 'VALUE_OLDER_THAN',
                  // Default to most common use case
                  identifier: `metric_${nextUnusedMetricID}`,
                  wiredState: '_raw',
                  // Default to _raw for VALUE_OLDER_THAN
                  category: 'connectivity',
                  // Default category for connectivity checks
                  description: 'No data received within specified time',
                  value: '604800' // Default to 7 days (604800 seconds)
                };
                updateField(healthBaseKey, [...currentHealthStates, emptyState]);
              }
            }
          }]
        };
        return [emptyState];
      }
      const valueField = state => {
        if (state.operator === 'VALUE_OLDER_THAN') {
          return {
            key: 'value',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.olderThanDuration'),
            type: 'select',
            options: this.availableDurations,
            required: true
          };
        }
        return {
          key: 'value',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
          required: true
        };
      };
      const wiredStateField = () => {
        const activeVersion = this.getActiveVersion(item);
        const parserVariables = this.getParserVariables(activeVersion);
        return {
          key: 'wiredState',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataState'),
          type: 'select',
          options: parserVariables,
          required: true // Make wiredState selection required
        };
      };
      const inputRows = [[{
        field: {
          key: 'type',
          label: this.$i18n_t('Common.type'),
          type: 'select',
          options: [{
            value: 'warning',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
          }, {
            value: 'error',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
          }]
        }
      }, {
        field: {
          key: 'category',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
          type: 'select',
          options: [{
            value: 'battery',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery')
          }, {
            value: 'connectivity',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity')
          }, {
            value: 'device',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device')
          }]
        }
      }, {
        field: {
          key: 'description',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.wizardDescriptionField'),
          required: true // Fix: Description is required as it becomes the error/warning message
        }
      }], [{
        field: wiredStateField
      }, {
        field: {
          type: 'select',
          key: 'operator',
          label: this.$i18n_t('Consumers.Filter.createDialog.operator'),
          required: true,
          // Fix: Operator is required for health evaluation
          options: [{
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.valueOlderThan'),
            value: 'VALUE_OLDER_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.equals'),
            value: 'EQUALS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.notEquals'),
            value: 'NOT_EQUALS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.greaterThan'),
            value: 'GREATER_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.lessThan'),
            value: 'LESS_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.contains'),
            value: 'CONTAINS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.notContains'),
            value: 'NOT_CONTAINS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.isNull'),
            value: 'IS_NULL'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.isNotNull'),
            value: 'IS_NOT_NULL'
          }]
        }
      }, {
        field: valueField
      }]];
      const healthStateRows = currentHealthStates.map((state, stateIndex) => {
        return {
          class: 'v-card v-sheet elevation-2 v-card__text',
          columns: [{
            rows: inputRows.map(inputRow => {
              const inputColumns = inputRow.map(input => {
                const computedField = typeof input.field === 'function' ? input.field(state) : input.field;
                return {
                  cols: input.cols,
                  sm: input.sm,
                  md: input.md,
                  lg: input.lg,
                  field: {
                    ...computedField,
                    key: `${healthBaseKey}[${stateIndex}].${computedField.key}`
                  }
                };
              });
              return {
                columns: inputColumns
              };
            })
          }, {
            class: 'flex-grow-0',
            rows: [{
              columns: [{
                field: {
                  type: 'button',
                  icon: 'fal fa-trash',
                  onClick: function (updateField) {
                    const newHealthStates = currentHealthStates.filter((_, index) => index !== stateIndex);
                    updateField(healthBaseKey, newHealthStates);
                  }
                }
              }]
            }]
          }]
        };
      });
      const addButtonRow = {
        columns: [{
          class: 'flex-grow-0 pl-0',
          field: {
            key: 'addButton',
            type: 'button',
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              const emptyState = {
                type: 'warning',
                // TODO: how to handle this?
                jsTransformer: '',
                wizard: true,
                operator: 'VALUE_OLDER_THAN',
                // Default to most common use case
                identifier: `metric_${nextUnusedMetricID}`,
                wiredState: '_raw',
                // Default to _raw for VALUE_OLDER_THAN
                category: 'connectivity',
                // Default category for connectivity checks
                description: 'No data received within specified time',
                value: '604800' // Default to 7 days (604800 seconds)
              };
              updateField(healthBaseKey, [...currentHealthStates, emptyState]);
            }
          }
        }]
      };
      const healthStateRowsWithSpacers = [];
      healthStateRows.forEach((row, index) => {
        healthStateRowsWithSpacers.push({
          subtitle: currentHealthStates[index].identifier
        });
        healthStateRowsWithSpacers.push(row);
        if (index >= healthStateRows.length - 1) {
          return;
        }
        healthStateRowsWithSpacers.push({
          isDivider: true
        });
      });
      return [...healthStateRowsWithSpacers, addButtonRow];
    },
    healthStateExpertTabRows(item) {
      const versions = get(item, 'versions', []);
      const defaultVersionIndex = versions.findIndex(v => v.version === 'default');
      const parserBaseKey = `versions[${defaultVersionIndex}].parser`;
      const healthBaseKey = `versions[${defaultVersionIndex}].healthStateConfig`;
      const selectedStatesPreview = Object.fromEntries(get(item, `${healthBaseKey}.stateKeys`, []).map(stateKey => {
        return [stateKey, {
          value: 'LAST_VALUE',
          time: 'ISO_TIMESTAMP_LAST_UPDATE'
        }];
      }));
      const currentMetrics = get(item, `${healthBaseKey}.health`, []);
      const metricsRows = currentMetrics.map((metric, metricIndex) => {
        return {
          subtitle: metric.identifier,
          columns: [
          // type
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('Common.type'),
              key: `${healthBaseKey}.health[${metricIndex}].type`,
              type: 'select',
              options: [
              // TODO: add all options
              {
                value: 'warning',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
              }, {
                value: 'error',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
              }]
            }
          },
          // category
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
              key: `${healthBaseKey}.health[${metricIndex}].category`,
              type: 'select',
              options: [
              // TODO: add all options
              {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery'),
                value: 'battery'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity'),
                value: 'connectivity'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device'),
                value: 'device'
              }]
            }
          },
          // identifier
          {
            cols: 12,
            sm: 12,
            md: 5,
            lg: 5,
            isSpacer: true
          },
          // trash button
          {
            cols: 12,
            sm: 12,
            md: 1,
            lg: 1,
            field: {
              type: 'button',
              icon: 'fal fa-trash',
              onClick: function (updateField) {
                updateField(`${healthBaseKey}.health`, currentMetrics.filter((_, index) => index !== metricIndex));
              }
            }
          }, {
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'healthStateExpertModeMetricCodeEditor',
            field: {
              key: `${healthBaseKey}.health[${metricIndex}].jsTransformer`
            }
          }]
        };
      });
      return [
      // select states
      {
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricDescription'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            label: this.$i18n_t('DeviceDriver.details.tabs.healthState.fields.stateKeys'),
            key: `${healthBaseKey}.stateKeys`,
            type: 'autocomplete',
            multiple: true,
            options: Object.entries(get(item, `${parserBaseKey}.config.variables`, {})).map(([key, value]) => ({
              label: get(value, 'name', key),
              value: key
            }))
          }
        }]
      },
      // quick tutorial / example
      {
        isExpansionPanel: true,
        headline: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.quickReferenceHeader'),
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricExample'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            type: 'code',
            fullWidth: true,
            content: `function (params) {
  // get battery voltage from params
  const batteryVoltage = params['battery_voltage'].value;

  // battery voltage is below 2.5V
  if (batteryVoltage < 2.5) {
    return "Battery almost empty";
  }

  // get current time/date
  const now = new Date();

  // 24hrs * 3600s/day * 1000ms/s
  const 24hoursInMilliseconds = 24 * 3600 * 1000;

  // get the last heartbeat time from params
  const lastHeartbeatTime = new Date(params['heartbeat'].time);

  // has not sent a heartbeat within 24 hours
  if ((now - lastHeartbeatTime) > 24hoursInMilliseconds) {
    return "Device seems offline";
  }

  return null;
}`
          }
        }]
      },
      // params preview
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            type: 'code',
            fullWidth: true,
            content: `params = ${JSON.stringify(selectedStatesPreview, null, 4)};`
          }
        }]
      }, ...metricsRows.map((row, rowIndex) => metricsRows.length > 1 && rowIndex !== metricsRows.length - 1 ? [row, {
        isDivider: true
      }] : [row]).flat(),
      // add another metric button
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 8,
          field: {
            type: 'button',
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentMetrics.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              updateField(`${healthBaseKey}.health`, [...currentMetrics, {
                identifier: `metric_${nextUnusedMetricID}`,
                wizard: false
              }]);
            }
          }
        }]
      }];
    },
    setLinkedDevicesTotalCountItems(payload) {
      this.linkedDevicesTotalCount = payload.totalCount;
    },
    headerButtons(currentItem) {
      let isLoading = false;
      return [{
        icon: 'fa fa-copy',
        title: this.$i18n_t('Common.copy'),
        isLoading,
        onClick: async () => {
          isLoading = true;
          try {
            navigator.clipboard.writeText(JSON.stringify(currentItem));
          } finally {
            isLoading = false;
          }
          this.showCopyPrompt = true;
        }
      }];
    },
    getDeviceImage(item) {
      const image = get(item, 'image', null);
      const attachments = get(item, 'properties.attachments', []);

      // if device image is in attachments, return the image url
      if (attachments.some(attachment => attachment.id === image)) {
        return this.getImageUrl(image);
      }

      // else, set the image to null because it was removed from the attachments
      // and return the placeholder image
      this.$set(item, 'image', null);
      return 'img/niota-classic/placeholder.png';
    }
  }
};